const toLegacyLocation = account => {
  const thryvId =
    account.thryvId ||
    account.thryvID ||
    account.account_id ||
    account.code ||
    account.id

  return {
    ...account,
    id: account.code || account.id || thryvId,
    code: account.code || account.accountCode || thryvId,
    thryvId,
    name:
      account.locationName ||
      account.company ||
      account.companyName ||
      account.name ||
      [account.firstName, account.lastName].filter(Boolean).join(' '),
    company: account.company || account.companyName || account.name,
    email: account.email || account.accountEmail,
    city: account.city || account.address?.city,
    stateCode:
      account.stateCode || account.address?.region || account.address?.state,
    country: account.country || account.address?.country,
    billTo: account.billTo ?? (account.parentAccountId ? 'parent' : 'self'),
    subscriptionId:
      account.subscriptionId ||
      account.recurlySubscriptionId ||
      account.RECURLY_SUBSCRIPTION_ID ||
      account.fieldData?.uuid,
    uuid: account.uuid || account.fieldData?.uuid,
    subs: account.hasActiveSubscription ? 1 : 0,
    subscriptions: account.subscriptions || [],
  }
}

const normalizeLegacyMyAccount = account => {
  const thryvId =
    account.thryvId ||
    account.account_id ||
    account.code ||
    account.id ||
    account.demo_appt_id
  const companyName =
    account.companyName ||
    account.company ||
    account.name ||
    account.cust_company ||
    ''
  const accountEmail =
    account.accountEmail || account.email || account.cust_email || ''

  return {
    ...account,
    thryvId,
    accountCode:
      account.accountCode || account.code || account.account_id || thryvId,
    companyName,
    company: account.company || companyName,
    accountEmail,
    email: account.email || accountEmail,
    firstName: account.firstName || account.cust_first_name || '',
    lastName: account.lastName || account.cust_last_name || '',
    phone: account.phone || account.cust_phone,
    city: account.city || account.address?.city,
    stateCode:
      account.stateCode || account.address?.region || account.address?.state,
    country: account.country || account.address?.country,
    isPre5: account.isPre5 ?? false,
    locations:
      account.locations?.length > 0
        ? account.locations
        : account.parentAccountId
        ? [toLegacyLocation(account)]
        : [],
  }
}

const accountMatches = (account, search) => {
  if (!search) return true
  const term = search.toLowerCase()
  return [
    account.companyName,
    account.company,
    account.name,
    account.accountEmail,
    account.email,
    account.thryvId,
    account.accountCode,
    account.demo_appt_id,
    account.cust_company,
    account.cust_email,
    account.cust_first_name,
    account.cust_last_name,
  ]
    .filter(Boolean)
    .some(value => String(value).toLowerCase().includes(term))
}

export const accountsApi = {
  getAccounts: async (client, {email, thryvId, eaid}) => {
    let query
    if (email) query = `email=${encodeURIComponent(email)}`
    else if (eaid) query = `eaid=${encodeURIComponent(eaid)}`
    else query = `thryvId=${encodeURIComponent(thryvId)}`
    try {
      const response = await client(`accounts/getAccounts?${query}`, {
        method: 'GET',
      })
      return (
        response.hierarchies ?? [
          {parent: response.parent, children: response.children ?? []},
        ]
      )
    } catch (err) {
      console.log('err getAccounts: ', err)
      const message =
        err?.response?.data?.response?.message ?? 'Failed to fetch accounts'
      throw new Error(message)
    }
  },

  getSubscriptions: async (client, {thryvId}) => {
    const response = await client(
      `accounts/5/subscriptions?thryvId=${thryvId}`,
      {
        method: 'GET',
      },
    )
    return response
  },

  getMyAccounts: async (client, {offset = 0, limit = 20, search}) => {
    const params = new URLSearchParams({offset, limit})
    if (search) params.set('search', search)
    const response = await client(
      `accounts/rep-accounts?${params.toString()}`,
      {
        method: 'GET',
      },
    )
    return response
  },

  getLegacyMyAccounts: async (client, {offset = 0, limit = 20, search}) => {
    const response = await client('accounts/mine?offset=0', {
      method: 'GET',
    })
    const allAccounts = (response.rows ?? [])
      .map(normalizeLegacyMyAccount)
      .filter(account => accountMatches(account, search))
    return {
      accounts: allAccounts.slice(offset, offset + limit),
      total: allAccounts.length,
      offset,
      limit,
    }
  },

  // Keap-partner-scoped accounts (book of business by keapPartnerId).
  // Same response shape as getMyAccounts so the My Accounts UI renders identically.
  getPartnerAccounts: async (
    client,
    {keapPartnerId, offset = 0, limit = 20, search},
  ) => {
    const params = new URLSearchParams({keapPartnerId, offset, limit})
    if (search) params.set('search', search)
    const response = await client(
      `accounts/partner-accounts?${params.toString()}`,
      {
        method: 'GET',
      },
    )
    return response
  },

  getAccountById: async (client, accountId) => {
    const response = await client(`accounts/account-details/${accountId}`, {
      method: 'GET',
    })
    return response.account
  },

  updateAccount: async (client, accountId, data) => {
    const response = await client(`accounts/account-details/${accountId}`, {
      method: 'PUT',
      data,
    })
    return response.account
  },
}
